import { createContext, useContext, useEffect, useState } from "react"

// SPEC-029 (RR4 #5): a labelled wrapper for one QC-lifecycle phase (Analyst / Reviewer / QA) in the
// "QC Results & Approvals" panel. All three phases are ALWAYS rendered (GMP/ALCOA+: the whole team
// SEES the full lifecycle). `empty` renders a neutral placeholder when the phase has no content
// yet. Purely presentational — the per-control act-gates (canSign) still govern who can act.
//
// #4142 — TWO INDEPENDENT AXES, not one. Ghosting used to be the plain inverse of the highlight
// (`active ? "is-active" : "is-ghosted"`), which made the lit-up step depend on the reader's role:
//   - `active`          = WHERE THE WORK IS. Decided by the run's state alone, so it is identical
//                         for every viewer. A non-active phase ghosts whole, exactly as before.
//   - `controlsGhosted` = "you cannot act on this right now". Only ever set on the ACTIVE phase,
//                         for a viewer who lacks that phase's role: the section keeps its full-
//                         strength highlight and its headings, hints and result tables stay
//                         readable, while its inputs and action buttons dim. Those controls are
//                         already `disabled` by their own act-gates — this is the visual half, so
//                         they stop looking like they would work.
// When nothing is actionable anywhere (a finished run), no phase is active and every section
// ghosts whole.
//
// #4106: the SECTION ghosting is an `opacity` below 1, and that does two things beyond dimming — it applies
// to EVERY descendant, and it makes the section its own stacking context. A modal opened from
// inside a ghosted phase therefore rendered translucent (the panel behind showing straight through
// it) and trapped underneath the rest of the panel, which no z-index on the modal can escape.
// Portalling it out is not an option here: the panel's styles are all nested under `.ResultEntryPanel`,
// so a modal moved to document.body would lose them. Instead a phase SUSPENDS its ghosting while a
// descendant has a modal open — the dimming only says "the work is not here", which a modal makes
// moot anyway.
//
// #4142 keeps this workaround — non-active phases still take section-level opacity, so a modal
// opened from one still needs it. It simply stops applying where people actually work: the ACTIVE
// phase no longer dims itself at all. The control-level ghosting suspends on the same signal, since
// the control containers take opacity too (the QA amend dialog's buttons sit inside one of them).
const PhaseOverlayContext = createContext(null)

// Called by a descendant that owns a modal: while `open`, the enclosing phase stops ghosting.
// Safe outside a PhaseSection (no-op), so a component can be reused off the panel.
export function usePhaseOverlay(open) {
  const setOverlay = useContext(PhaseOverlayContext)
  useEffect(() => {
    if (!setOverlay) return undefined
    setOverlay(open)
    return () => setOverlay(false)
  }, [open, setOverlay])
}

export default function PhaseSection({
  title,
  stepLabel = null,
  active = false,
  controlsGhosted = false,
  empty = false,
  hint = null,
  children,
}) {
  const [overlay, setOverlay] = useState(false)
  const className = [
    "ResultEntryPanel-phase",
    active ? "is-active" : "is-ghosted",
    // Only meaningful on the active phase — a ghosted section already dims its controls with
    // everything else, and stacking two opacities on them would dim them twice over.
    active && controlsGhosted ? "is-controls-ghosted" : "",
    empty ? "is-empty" : "",
    overlay ? "has-overlay" : "",
  ]
    .filter(Boolean)
    .join(" ")
  return (
    <PhaseOverlayContext.Provider value={setOverlay}>
      <section className={className}>
        <header className="ResultEntryPanel-phase-head">
          {stepLabel && <span className="ResultEntryPanel-phase-step">{stepLabel}</span>}
          <h4 className="ResultEntryPanel-phase-title">{title}</h4>
          {hint && <span className="ResultEntryPanel-phase-hint">{hint}</span>}
        </header>
        <div className="ResultEntryPanel-phase-body">{children}</div>
      </section>
    </PhaseOverlayContext.Provider>
  )
}
